import {
  Storage,
  StorageKey,
  StorageKind,
  StorageSubscribe,
} from '@revelio/core';

import type { PrimaryFilterId, TreeSelection } from '../types/filters';
import { getPrimarySelectionMatchKey } from './primaryFilterUtils';

/**
 * More than the picker shows collapsed (`COLLAPSED_CHIP_COUNT`) — the rest are
 * a click on the "+N" reveal away.
 */
export const MAX_RECENT_ENTITIES = 10;

type RecentEntitiesRecord = {
  entities: TreeSelection[];
  version?: string;
};

export const getRecentEntitiesStorageKey = (
  userId: string,
  filterId: PrimaryFilterId
): string =>
  `${StorageKey.RecentEntitiesPrefix}${userId}-${filterId.toLowerCase()}`;

const sanitize = ({ id, name, levelId }: TreeSelection): TreeSelection => ({
  id,
  name,
  levelId,
});

export const getRecentEntities = (
  userId: string,
  filterId: PrimaryFilterId,
  version?: string
): TreeSelection[] => {
  const record = Storage.readJson<RecentEntitiesRecord>(
    StorageKind.Local,
    getRecentEntitiesStorageKey(userId, filterId),
    { entities: [] }
  );
  if (version !== undefined && record.version !== version) {
    return [];
  }
  if (!Array.isArray(record.entities)) return [];
  // Clamped on the way out too: a reader who filled the old, larger list keeps
  // it in storage until their next selection rewrites it.
  return record.entities.slice(0, MAX_RECENT_ENTITIES);
};

const writeRecentEntities = (
  userId: string,
  filterId: PrimaryFilterId,
  entities: TreeSelection[],
  version?: string
): void => {
  const storageKey = getRecentEntitiesStorageKey(userId, filterId);
  Storage.writeJson<RecentEntitiesRecord>(StorageKind.Local, storageKey, {
    entities,
    version,
  });
  // Same-document writes do not raise a `storage` event, so tell subscribers
  // (`useRecentEntities`) directly that this key changed.
  StorageSubscribe.notify(StorageKind.Local, storageKey);
};

export const addRecentEntities = (
  userId: string,
  filterId: PrimaryFilterId,
  entities: TreeSelection[],
  version?: string
): void => {
  if (entities.length === 0) return;
  const addedKeys = new Set(entities.map(getPrimarySelectionMatchKey));
  const kept = getRecentEntities(userId, filterId, version).filter(
    (entity) => !addedKeys.has(getPrimarySelectionMatchKey(entity))
  );
  const merged = [...entities.map(sanitize), ...kept].slice(
    0,
    MAX_RECENT_ENTITIES
  );
  writeRecentEntities(userId, filterId, merged, version);
};

export const removeRecentEntities = (
  userId: string,
  filterId: PrimaryFilterId,
  entities: Pick<TreeSelection, 'id' | 'levelId'>[],
  version?: string
): void => {
  if (entities.length === 0) return;
  const removedKeys = new Set(entities.map(getPrimarySelectionMatchKey));
  const kept = getRecentEntities(userId, filterId, version).filter(
    (entity) => !removedKeys.has(getPrimarySelectionMatchKey(entity))
  );
  writeRecentEntities(userId, filterId, kept, version);
};
